import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuCircleX, LuClipboardCheck, LuRotateCcw } from 'react-icons/lu';
import Button from '../ui/Button';
import GlassIcon from '../ui/GlassIcon';
import Modal from '../ui/Modal';
import { useTranslation } from '../../i18n/LanguageContext';
import { drawQuizQuestions, gradeQuiz, recordQuizAttempt } from '../../utils/questionPool';
import QuizChoice from './QuizChoice';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

/**
 * Okuma sonu sınavı: çalışanın departman havuzundan rastgele sorular sorar.
 * Geçilirse `onPassed` çağrılır; kalınırsa yeni sorularla tekrar denenir.
 * Havuzda soru yoksa sınav atlanır ve `onPassed({ skipped: true })` çağrılır.
 */
const PoolQuiz = ({ show, group, source, user, onPassed, onClose }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.quiz.${key}`, vars);
  const [phase, setPhase] = useState('loading'); // loading | error | quiz | result
  const [questions, setQuestions] = useState([]);
  const [passScore, setPassScore] = useState(70);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const startAttempt = useCallback(async () => {
    setPhase('loading');
    setIndex(0);
    setAnswers({});
    setResult(null);
    try {
      const drawn = await drawQuizQuestions(group);
      if (drawn.questions.length === 0) {
        onPassed({ skipped: true });
        return;
      }
      setQuestions(drawn.questions);
      setPassScore(drawn.passScore);
      setPhase('quiz');
    } catch (error) {
      console.error('Sınav soruları yüklenemedi:', error);
      setPhase('error');
    }
  }, [group, onPassed]);

  useEffect(() => {
    if (show) startAttempt();
    // Yalnızca pencere açıldığında yeni deneme başlar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const current = questions[index];
  const answeredAll = questions.every((question) => answers[question.id] !== undefined);

  const finish = async () => {
    const graded = gradeQuiz(questions, answers, passScore);
    setResult(graded);
    setPhase('result');
    await recordQuizAttempt({
      userId: user?.uid || '',
      employeeId: user?.employeeId || '',
      employeeName: user?.name || '',
      group: group || '',
      sourceType: source.type,
      sourceId: source.id,
      sourceName: source.name || '',
      questionIds: questions.map((question) => question.id),
      questionCount: questions.length,
      correctCount: graded.correctCount,
      score: graded.score,
      passScore,
      passed: graded.passed,
    });
  };

  let footer = null;
  if (phase === 'quiz' && current) {
    footer = (
      <>
        <Button variant="ghost" onClick={() => setIndex(index - 1)} disabled={index === 0}>{label('previous')}</Button>
        {index < questions.length - 1 ? (
          <Button variant="primary" onClick={() => setIndex(index + 1)} disabled={answers[current.id] === undefined} className="max-sm:tw-flex-1">
            {label('next')}
          </Button>
        ) : (
          <Button variant="primary" onClick={finish} disabled={!answeredAll} className="max-sm:tw-flex-1">{label('finish')}</Button>
        )}
      </>
    );
  } else if (phase === 'result' && result) {
    footer = result.passed ? (
      <Button variant="success" onClick={() => onPassed({ score: result.score })} className="max-sm:tw-flex-1">
        <LuCircleCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
        {label('complete')}
      </Button>
    ) : (
      <>
        <Button variant="ghost" onClick={onClose}>{label('backToDocument')}</Button>
        <Button variant="primary" onClick={startAttempt} className="max-sm:tw-flex-1">
          <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('retry')}
        </Button>
      </>
    );
  }

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={onClose}
      size="lg"
      title={label('title')}
      subtitle={source.name}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30" size="xs">
          <LuClipboardCheck aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={footer}
    >
      {phase === 'loading' && (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-12 tw-text-center">
          <span className="tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-4 tw-border-teal-500/20 tw-border-t-teal-500" />
          <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('loading')}</p>
        </div>
      )}

      {phase === 'error' && (
        <div role="alert" className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-10 tw-text-center">
          <p className="tw-text-sm tw-text-rose-700 dark:tw-text-rose-300">{label('loadError')}</p>
          <Button variant="primary" onClick={startAttempt}>
            <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" />
            {t('common.retry')}
          </Button>
        </div>
      )}

      {phase === 'quiz' && current && (
        <div className="tw-space-y-5">
          <div className="tw-space-y-2">
            <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">
              <span>{label('questionOf', { current: index + 1, total: questions.length })}</span>
              <span>{label('passScore', { score: passScore })}</span>
            </div>
            <span className="tw-block tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
              <span className="tw-block tw-h-full tw-rounded-full tw-bg-teal-500 tw-transition-[width] tw-duration-300" style={{ width: `${((index + 1) / questions.length) * 100}%` }} />
            </span>
          </div>
          <h3 className="tw-text-lg tw-font-semibold tw-leading-snug tw-text-slate-900 dark:tw-text-white">{current.question}</h3>
          <div role="radiogroup" aria-label={label('choices')} className="tw-space-y-2">
            {current.options.map((option, optionIndex) => (
              <QuizChoice
                // Şıkların kalıcı kimliği yok; sıra deneme boyunca sabittir
                key={optionIndex}
                letter={LETTERS[optionIndex]}
                state={answers[current.id] === optionIndex ? 'selected' : 'idle'}
                onClick={() => setAnswers((prev) => ({ ...prev, [current.id]: optionIndex }))}
              >
                {option}
              </QuizChoice>
            ))}
          </div>
        </div>
      )}

      {phase === 'result' && result && (
        <div className="tw-space-y-5">
          <div className={`tw-flex tw-items-center tw-gap-4 tw-rounded-2xl tw-p-4 ${result.passed ? 'tw-bg-emerald-500/10' : 'tw-bg-rose-500/10'}`}>
            <span className={`tw-grid tw-h-16 tw-w-16 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-white ${result.passed ? 'tw-bg-emerald-500' : 'tw-bg-rose-500'}`}>
              {result.passed ? <LuCircleCheck aria-hidden="true" className="tw-h-8 tw-w-8" /> : <LuCircleX aria-hidden="true" className="tw-h-8 tw-w-8" />}
            </span>
            <div className="tw-min-w-0">
              <p className={`tw-text-3xl tw-font-bold tw-tabular-nums ${result.passed ? 'tw-text-emerald-700 dark:tw-text-emerald-300' : 'tw-text-rose-700 dark:tw-text-rose-300'}`}>
                {label('percent', { value: result.score })}
              </p>
              <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
                {label('correctOf', { correct: result.correctCount, total: questions.length })} · {result.passed ? label('passed') : label('passScore', { score: passScore })}
              </p>
            </div>
          </div>
          {!result.passed && <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{label('failHint')}</p>}

          <ul className="tw-space-y-2">
            {questions.map((question, questionIndex) => {
              const correct = answers[question.id] === question.correctAnswer;
              return (
                <li key={question.id} className={`tw-flex tw-gap-3 tw-rounded-2xl tw-border tw-p-3 ${correct ? 'tw-border-emerald-500/25 tw-bg-emerald-500/[0.05]' : 'tw-border-rose-500/25 tw-bg-rose-500/[0.05]'}`}>
                  {correct
                    ? <LuCircleCheck aria-hidden="true" className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-emerald-500" />
                    : <LuCircleX aria-hidden="true" className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-rose-500" />}
                  <div className="tw-min-w-0 tw-space-y-1">
                    <p className="tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{questionIndex + 1}. {question.question}</p>
                    {/* Kalan kullanıcıya doğru cevap gösterilmez: aynı soru tekrar çıkabilir */}
                    {(correct || result.passed) && (
                      <p className="tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">{label('correctAnswer', { answer: question.options[question.correctAnswer] })}</p>
                    )}
                    {result.passed && question.explanation && <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{question.explanation}</p>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Modal>
  );
};

PoolQuiz.propTypes = {
  show: PropTypes.bool,
  group: PropTypes.string,
  source: PropTypes.shape({ type: PropTypes.string, id: PropTypes.string, name: PropTypes.string }).isRequired,
  user: PropTypes.object,
  onPassed: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PoolQuiz;
